import React, {createContext, useContext, useEffect, useState} from 'react';

interface CookieConsentContextT {
  /** True if the user has analytics consent */
  hasAnalyticsConsent: boolean;
  /** True if the user has marketing consent */
  hasMarketingConsent: boolean;
  /** True if the user has preferences consent */
  hasPreferencesConsent: boolean;
  /** True if the CookieBot is initialized */
  isInitialized: boolean;
}

/**
 * Cookie Consent Context
 * @type {CookieConsentContextT}
 */
const CookieConsentContext = createContext<CookieConsentContextT>({
  hasAnalyticsConsent: false,
  hasMarketingConsent: false,
  hasPreferencesConsent: false,
  isInitialized: false,
});

/**
 * Cookie Consent Provider
 * @param children - React.ReactNode
 * @param cookieBotId - string
 * @returns React.FC
 */
export const CookieConsentProvider: React.FC<{children: React.ReactNode; cookieBotId: string}> = ({
  children,
  cookieBotId,
}) => {
  const [consent, setConsent] = useState<CookieConsentContextT>({
    hasAnalyticsConsent: false,
    hasMarketingConsent: false,
    hasPreferencesConsent: false,
    isInitialized: false,
  });

  useEffect(() => {
    // Initialize Google Consent Mode
    const consentScript = document.createElement('script');
    consentScript.setAttribute('data-cookieconsent', 'ignore');
    consentScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      gtag("consent", "default", {
        ad_personalization: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        analytics_storage: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",
        wait_for_update: 500,
      });
      gtag("set", "ads_data_redaction", true);
      gtag("set", "url_passthrough", false);
    `;
    document.head.insertBefore(consentScript, document.head.firstChild);

    // Initialize CookieBot
    const cookiebotScript = document.createElement('script');
    cookiebotScript.id = 'Cookiebot';
    cookiebotScript.src = 'https://consent.cookiebot.com/uc.js';
    cookiebotScript.setAttribute('data-cbid', cookieBotId);
    cookiebotScript.setAttribute('data-blockingmode', 'auto');
    cookiebotScript.async = true;
    document.head.appendChild(cookiebotScript);

    // Listen for CookieBot consent changes
    window.addEventListener('CookiebotOnAccept', updateConsent);
    window.addEventListener('CookiebotOnDecline', updateConsent);

    return () => {
      window.removeEventListener('CookiebotOnAccept', updateConsent);
      window.removeEventListener('CookiebotOnDecline', updateConsent);
      document.head.removeChild(cookiebotScript);
      document.head.removeChild(consentScript);
    };
  }, [cookieBotId]);

  const updateConsent = () => {
    if (window.Cookiebot) {
      const newConsent = {
        hasAnalyticsConsent: window.Cookiebot.consent.statistics,
        hasMarketingConsent: window.Cookiebot.consent.marketing,
        hasPreferencesConsent: window.Cookiebot.consent.preferences,
        isInitialized: true,
      };

      // Update Google consent mode when consent changes
      window.dataLayer.push({
        event: 'consent_update',
        analytics_storage: newConsent.hasAnalyticsConsent ? 'granted' : 'denied',
        ad_storage: newConsent.hasMarketingConsent ? 'granted' : 'denied',
        functionality_storage: newConsent.hasPreferencesConsent ? 'granted' : 'denied',
        personalization_storage: newConsent.hasPreferencesConsent ? 'granted' : 'denied',
      });

      setConsent(newConsent);
    }
  };

  return <CookieConsentContext.Provider value={consent}>{children}</CookieConsentContext.Provider>;
};

/**
 * Hook to use the CookieConsentContext
 * @returns CookieConsentContextT
 */
export const useCookieConsent = (): CookieConsentContextT => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
